import { useMemo, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import {
  useCreateIncidentMutation,
  useFacilityEmergencyCallingQuery,
  useFacilityNamesQuery,
} from '@/graphql/generated/hooks'
import { IncidentStatus } from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { openIncidentDrawer, setIncident } from '@/redux/ui/uiSlice'
import { LabelValueIProps } from '@/utils/filterDrawers'
import { getFacilityOptions } from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT, SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

interface UseEmergencyCallDataIProps {
  emergencyCallCategoryId: string
  facility: LabelValueIProps
  handleOnClick: (
    facilityId?: string,
    facilityName?: string,
    incidentId?: string,
    incidentDisplayId?: string,
    emergencyNumber?: string
  ) => void
  onClose: () => void
}

export const useEmergencyCallData = ({
  emergencyCallCategoryId,
  facility,
  handleOnClick,
  onClose,
}: UseEmergencyCallDataIProps) => {
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )
  const dispatch = useDispatch()
  const me = useSelector(selectMe)
  const { showSuccess, showError } = useToasts()
  const [createIncident, { loading: isCreatingIncident }] =
    useCreateIncidentMutation()

  // Fetch all facilities for dropdown
  const { data: facilitiesData, loading: isLoadingFacilities } =
    useFacilityNamesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter:
          debouncedfacilityName?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilityName }
            : undefined,
      },
    })

  // Fetch emergency calling config for selected facility
  const { data: facilityData, loading: isLoadingFacilityEmergencyCallingInfo } =
    useFacilityEmergencyCallingQuery({
      fetchPolicy: 'network-only',
      variables: { id: facility?.value },
      skip: !facility?.value,
    })

  const handleCreateIncidentAndCall = async (facilityId: string) => {
    try {
      if (!emergencyCallCategoryId) {
        showError('Emergency call category not found. Please try again.')
        return
      }

      const incidentResponse = await createIncident({
        variables: {
          input: {
            creatorId: me?.id,
            ownerId: me?.id,
            facilityId: facilityId,
            manualIncidentCategoryId: emergencyCallCategoryId,
            name: 'Emergency Call',
            description:
              'Automated Incident created via emergency call button.',
            status: IncidentStatus.InProgress,
          },
        },
        refetchQueries: ['GetIncidents', 'GetOutstandingIncidents'],
      })

      const incidentId = incidentResponse.data?.createIncident?.incident?.id
      const displayId =
        incidentResponse.data?.createIncident?.incident?.displayId

      if (!incidentId) {
        showError('Failed to create emergency incident. Please try again.')
        return
      }

      showSuccess(`Emergency incident ${displayId} created successfully`)
      handleOnClick(
        facilityId,
        facility?.label,
        incidentId,
        displayId,
        emergencyNumber
      )

      dispatch(setIncident({ id: incidentId }))
      dispatch(openIncidentDrawer())

      onClose()
    } catch (error) {
      showError(
        'Failed to create emergency incident. The call will still be placed.'
      )

      handleOnClick(facilityId, facility?.label)
      onClose()
    }
  }

  // Format facility options for dropdown
  const facilityOptions = useMemo(
    () => getFacilityOptions(facilitiesData),
    [facilitiesData]
  )

  // Get emergency number from facility query
  const emergencyNumber = facilityData?.facility?.emergencyNumber || null

  const outboundPhoneNumber =
    facilityData?.facility?.emergencyCalling?.outboundPhoneNumber || null

  const isEmergencyCallingConfigured = Boolean(outboundPhoneNumber)

  return {
    facilityOptions,
    isCreatingIncident,
    isLoadingFacilities,
    isLoadingFacilityDetails: isLoadingFacilityEmergencyCallingInfo,
    emergencyNumber,
    outboundPhoneNumber,
    isEmergencyCallingConfigured,
    handleCreateIncidentAndCall,
    setFacilityName,
  }
}
